// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { type FC, useEffect, useState } from "react";
import { Button, Box, Column, Text, TextInput } from "@hightouch/htui";
import { captureException } from "src/lib/monitoring";
import { useParams } from "src/router";
import { AuthLayout } from "src/components/auth/auth-layout";
import { Terms } from "src/components/auth/terms";
import { useAuthState } from "src/components/auth/use-auth-state";
import { PartnerConnectHeader } from "src/pages/partner-connect";
import { AuthCard } from "src/components/auth/auth-card";
import { OtherOptions } from "src/components/auth/other-options";
import { buildReturnToUrl } from "src/utils/auth-redirect";
import { TOO_MANY_ATTEMPTS_MESSAGE } from "src/components/auth/util";

export const SSO: FC = () => {
  const { returnTo, partnerInformation, error: queryError } = useAuthState();

  const { org } = useParams<{ org: string }>();
  const [organization, setOrganization] = useState<string | undefined>(org);
  const [_error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const getAuth0AuthUrl = (
    organizationSlug: string,
    connectionName: string,
  ) => {
    const auth0AuthUrl = new URL(
      `${
        import.meta.env.VITE_API_BASE_URL
      }/auth0/oauth/${organizationSlug}/${connectionName}`,
    );
    if (returnTo) {
      auth0AuthUrl.searchParams.append("returnTo", returnTo);
    }
    return auth0AuthUrl;
  };

  const getAuth0Connections = async () => {
    if (!organization) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/connections`,
        {
          method: "POST",
          mode: "cors",
          cache: "no-cache",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ slug: organization }),
        },
      );
      if (response.status === 429) {
        setError(TOO_MANY_ATTEMPTS_MESSAGE);
        return;
      }
      const connections = await response.json();
      const connection = connections?.[0];
      if (connection) {
        const url = getAuth0AuthUrl(organization, connection.name).toString();
        window.open(url);
      } else {
        setError("No organization with that identifier was found.");
      }
    } catch (error) {
      setError(error);
      captureException(error);
    } finally {
      setLoading(false);
    }
  };

  const error = queryError || _error;

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setError("");
  }, [organization]);

  return (
    <>
      <AuthLayout metaTitle="Single sign-on">
        <Column>
          <AuthCard heading="Enter your organization identifier" gap={6}>
            {partnerInformation && (
              <PartnerConnectHeader partnerInformation={partnerInformation} />
            )}
            <Column
              as="form"
              gap={4}
              onSubmit={(event) => {
                event.preventDefault();
                getAuth0Connections();
              }}
            >
              <Column gap={2}>
                <TextInput
                  autoFocus
                  isInvalid={Boolean(error)}
                  width="100%"
                  placeholder="Organization identifier…"
                  value={organization || ""}
                  onChange={(event) => {
                    setOrganization(event.target.value);
                  }}
                />

                {error && (
                  <Box as={Text} color="danger.base" maxWidth="60ch">
                    {error}
                  </Box>
                )}
              </Column>

              <Button
                isDisabled={!organization}
                isLoading={loading}
                size="lg"
                type="submit"
                variant="primary"
              >
                Continue with SSO
              </Button>
            </Column>

            <Terms />

            <OtherOptions
              variant="link"
              to={returnTo ? buildReturnToUrl("/login", returnTo) : "/login"}
            />
          </AuthCard>
        </Column>
      </AuthLayout>
    </>
  );
};
